import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../../requestOptions';

const validate = (runId: number, subscriptionId: number, key: string) => {
  if (!runId || runId < 1) {
    throw new Error('Valid run ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  if (!key) {
    throw new Error('A key is required');
  }
};

type RequestParams = {
  runId: number;
  /** Any subscription of the organization — see `getRunFamilies`. */
  subscriptionId: number;
  /** The next step's commonality key. */
  key: string;
};

export type NextStepCsv = { blob: Blob; filename: string };

/** The server's filename when the response exposes one (`Content-Disposition`), else ours. */
export const nextStepCsvFilename = (contentDisposition: string | null | undefined, runId: number, key: string): string => {
  const quoted = contentDisposition?.match(/filename="([^"]+)"/)?.[1];
  const bare = contentDisposition?.match(/filename=([^;"]+)/)?.[1]?.trim();
  if (quoted || bare) return (quoted || bare) as string;
  const slug =
    key
      .replace(/[^A-Za-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'key';
  return `expansion-${runId}-${slug}.csv`;
};

/**
 * A fill-in template for one next step: product id, sku, title, parent sku, and an empty column
 * named after the attribute. Read as a blob so it goes through the scoped client (auth, org header)
 * rather than a bare link the browser would fetch without either.
 */
export const getRunNextStepCsv = async (
  scope: RequestScope,
  { runId, subscriptionId, key }: RequestParams,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<NextStepCsv>> => {
  try {
    validate(runId, subscriptionId, key);

    // Each segment encoded but the slashes kept: the route takes everything between next-steps/ and
    // /products.csv as the key ("Size / Fit" included), and an encoded slash is refused by some proxies.
    const path = key.split('/').map(encodeURIComponent).join('/');
    const result = await scopedChannelApi(scope).get<Blob>(`atts/expansion/runs/${runId}/next-steps/${path}/products.csv`, {
      ...options,
      responseType: 'blob',
      headers: { 'x-astur-subscription-id': subscriptionId.toString() },
    });
    const disposition = (result.headers as Record<string, unknown> | undefined)?.['content-disposition'];

    return {
      data: {
        blob: result.data instanceof Blob ? result.data : new Blob([result.data as BlobPart], { type: 'text/csv' }),
        filename: nextStepCsvFilename(typeof disposition === 'string' ? disposition : null, runId, key),
      },
      status: result.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
